import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { RecordResponse } from '@/models/ServiceTypes';
import { Role } from '@/models/AccountEnums';
import type {
  FirmHomeInstitution,
  HomeAwayInstitution,
} from '@/modules/dataFeeds/types';
import { mapFirmAggregatorName } from '@/modules/dataFeeds/utils/mapFirmAggregatorName';
import { aggregatorQuery, waServicesQuery } from './baseQueries';
import type { WARootState } from './store';

type QueryNoArgs = Record<string, never>;

export interface RefreshFirmAccountsArgs {
  aggregatorName: string;
  advisorGroupWealthId: number;
}

function assertFirmRole(state: WARootState): FetchBaseQueryError | undefined {
  if (state.auth.currentRole !== Role.Firm) {
    return {
      status: 403,
      data: 'Data feeds are only available for firm role',
    };
  }
  return undefined;
}

function assertAdvisorRole(
  state: WARootState,
): FetchBaseQueryError | undefined {
  if (state.auth.currentRole !== Role.Advisor) {
    return {
      status: 403,
      data: 'Data feeds are only available for advisor role',
    };
  }
  return undefined;
}

export const dataFeedsApi = createApi({
  reducerPath: 'dataFeedsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['DataFeeds', 'HomeInstitutions'],
  endpoints: (builder) => ({
    getDataFeeds: builder.query<
      RecordResponse<FirmHomeInstitution>,
      QueryNoArgs
    >({
      queryFn: async (_args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertFirmRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: '/api/firm/dataFeeds',
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<FirmHomeInstitution> };
      },
      providesTags: [{ type: 'DataFeeds', id: 'LIST' }],
    }),
    getHomeInstitutions: builder.query<
      RecordResponse<FirmHomeInstitution>,
      QueryNoArgs
    >({
      queryFn: async (_args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertFirmRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: '/api/firm/HomeInstitutions',
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<FirmHomeInstitution> };
      },
      providesTags: [{ type: 'HomeInstitutions', id: 'LIST' }],
    }),
    /**
     * Triggers Get Latest Balances for a firm feed
     * (`GET {AggregatorApiUrl}/api/Firm/{Aggregator}/Accounts/{AdvisorGroupWealthId}`).
     */
    refreshFirmAccounts: builder.mutation<undefined, RefreshFirmAccountsArgs>({
      queryFn: async (
        { aggregatorName, advisorGroupWealthId },
        api,
        extraOptions,
      ) => {
        const state = api.getState() as WARootState;
        const roleError = assertFirmRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const segment = mapFirmAggregatorName(aggregatorName);
        if (!segment) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: 'Aggregator name is required' },
              error: 'Aggregator name is required',
            },
          };
        }

        if (
          !Number.isFinite(advisorGroupWealthId) ||
          advisorGroupWealthId <= 0
        ) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: 'AdvisorGroupWealthId is required' },
              error: 'AdvisorGroupWealthId is required',
            },
          };
        }

        const result = await aggregatorQuery(
          {
            url: `/api/Firm/${encodeURIComponent(segment)}/Accounts/${String(advisorGroupWealthId)}`,
            method: 'GET',
          },
          api,
          extraOptions,
        );

        if (result.error) {
          return { error: result.error };
        }

        return { data: undefined };
      },
    }),
    getAdvisorDataFeeds: builder.query<
      RecordResponse<FirmHomeInstitution>,
      QueryNoArgs
    >({
      queryFn: async (_args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertAdvisorRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: '/api/advisor/HomeInstitutions',
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<FirmHomeInstitution> };
      },
      providesTags: [{ type: 'DataFeeds', id: 'ADVISOR_LIST' }],
    }),
    renameAdvisorDataFeed: builder.mutation<
      FirmHomeInstitution,
      { AdvisorInstitutionWealthId: number; Name: string }
    >({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertAdvisorRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: `/api/advisor/HomeInstitutions/${String(arg.AdvisorInstitutionWealthId)}`,
          method: 'PUT',
          body: arg,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as FirmHomeInstitution };
      },
      invalidatesTags: [{ type: 'DataFeeds', id: 'ADVISOR_LIST' }],
    }),
    deleteAdvisorDataFeed: builder.mutation<
      undefined,
      { AdvisorInstitutionWealthId: number }
    >({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertAdvisorRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: `/api/advisor/HomeInstitutions/${String(arg.AdvisorInstitutionWealthId)}`,
          method: 'DELETE',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: undefined };
      },
      invalidatesTags: [{ type: 'DataFeeds', id: 'ADVISOR_LIST' }],
    }),
    // The aggregator pull runs synchronously, so this request stays open for
    // the duration — callers show progress rather than fire-and-forget.
    resyncAdvisorDataFeed: builder.mutation<
      undefined,
      { AdvisorInstitutionWealthId: number }
    >({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertAdvisorRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: `/api/advisor/HomeInstitutions/${String(arg.AdvisorInstitutionWealthId)}/resync`,
          method: 'POST',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: undefined };
      },
      invalidatesTags: [{ type: 'DataFeeds', id: 'ADVISOR_LIST' }],
    }),
    // The advisor POST route does not exist yet, so this 404s and the modal
    // surfaces the error.
    addAdvisorDataFeed: builder.mutation<
      FirmHomeInstitution,
      { FinancialInstitutionId: number }
    >({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertAdvisorRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: '/api/advisor/HomeInstitutions',
          method: 'POST',
          body: arg,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as FirmHomeInstitution };
      },
      invalidatesTags: [{ type: 'DataFeeds', id: 'ADVISOR_LIST' }],
    }),
    searchAdvisorInstitutions: builder.query<
      RecordResponse<HomeAwayInstitution>,
      { searchTerm: string }
    >({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertAdvisorRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: '/api/advisor/Institutions',
          method: 'GET',
          params: { SearchTerm: arg.searchTerm },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<HomeAwayInstitution> };
      },
    }),
  }),
});

export const {
  useGetDataFeedsQuery,
  useGetHomeInstitutionsQuery,
  useRefreshFirmAccountsMutation,
  useGetAdvisorDataFeedsQuery,
  useRenameAdvisorDataFeedMutation,
  useDeleteAdvisorDataFeedMutation,
  useResyncAdvisorDataFeedMutation,
  useAddAdvisorDataFeedMutation,
  useSearchAdvisorInstitutionsQuery,
} = dataFeedsApi;
